import { Box, Paper, Stack, Tooltip, Typography } from '@mui/material'
import { CarrierVisitDirection, OrderResponseDto, UnitLabelType } from '@planning/app/api'
import { HoldsIconByJourneyId } from '@planning/components/hold/DisplayHoldsButton'
import IconFullOrEmptyContainer from '@planning/components/icon/IconFullOrEmptyContainer'
import { useTranslate } from '@tolgee/react'
import {
  ColoredLabel,
  DamagedIcon,
  DangerousIcon,
  ReeferIcon,
  useMinimalsTheme,
  WarningIcon,
} from '@tom-ui/ui'
import { ContainerNumber } from '@tom-ui/utils'
import { computed } from 'mobx'
import { ReactNode } from 'react'
import { ContainerJourneyStatus, ContainerJourneyStatusChip } from '../ContainerJourneyStatusChip'

interface Props {
  id: number
  number: string
  isoCode: string
  weight?: number
  status: ContainerJourneyStatus
  size?: number | null
  direction?: CarrierVisitDirection

  pod?: string | null
  pol?: string | null
  shippingLine?: string | null
  consignee?: string | null
  referenceNumber?: string | null

  archetype?: string | null
  weightClass?: string | null
  category?: string | null
  finalDestination?: string | null
  inboundVisit?: string | null
  outboundVisit?: string | null

  isEmpty?: boolean
  isOog?: boolean
  isDangerous?: boolean
  isReefer?: boolean
  isDamaged?: boolean
  hasHolds?: boolean
  hasIssue?: boolean
  isReserved?: boolean

  orderData?: OrderResponseDto

  isHighlighting?: boolean
  highlight?: boolean
  schemeBackgroundColor?: string

  renderActions?: ReactNode
}

export const ContainerDetailsCard = ({
  number,
  isoCode,
  weight,
  status,
  pod,
  pol,
  direction,
  shippingLine,
  consignee,
  referenceNumber,
  archetype,
  weightClass,
  category,
  finalDestination,
  inboundVisit,
  outboundVisit,
  isEmpty,
  isOog,
  isDangerous,
  isReefer,
  isDamaged,
  hasHolds,
  hasIssue,
  isReserved,
  orderData,
  isHighlighting,
  highlight,
  schemeBackgroundColor,
  renderActions,
}: Props) => {
  const theme = useMinimalsTheme()
  const { t } = useTranslate()

  const iconBoxStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    color: 'white',
    fontWeight: 'bold',
    fontSize: '0.75rem',
    height: '20px',
    minWidth: '20px',
    padding: '0 4px',
  }

  const dangerousTooltip = (() => {
    const imoLine = orderData?.imoClasses?.length ? `IMO: ${orderData.imoClasses.join(', ')}` : null

    const unNumbers = [
      ...new Set(
        (orderData?.dangerousGoodsDefinitions ?? [])
          .map(d => d.unNumber)
          .filter((n): n is string => !!n),
      ),
    ]
    const unLine = unNumbers.length ? `UN: ${unNumbers.join(', ')}` : null

    const otherLabelsLine = orderData?.otherLabels?.length
      ? orderData.otherLabels
          .map(label => {
            switch (label) {
              case UnitLabelType.MarinePollutant:
                return t('marinePollutant', 'Marine Pollutant')
              case UnitLabelType.FumigationWarning:
                return t('fumigationWarning', 'Fumigation Warning')
              case UnitLabelType.ElevatedTemperature:
                return t('elevatedTemperature', 'Elevated Temperature')
              case UnitLabelType.LimitedQuantities:
                return t('limitedQuantities', 'Limited Quantities')
              default:
                return ''
            }
          })
          .join(' · ')
      : null

    const extraLines = [imoLine, unLine, otherLabelsLine].filter(Boolean)
    if (!extraLines.length) return t('dangerousGoods', 'Dangerous goods')

    return (
      <>
        <div>{t('dangerousGoods', 'Dangerous goods')}</div>
        {extraLines.map((line, i) => (
          <div key={i}>{line}</div>
        ))}
      </>
    )
  })()

  const oogTooltip = (() => {
    const lines: string[] = []

    if (orderData?.overWidthRight || orderData?.overWidthLeft) {
      lines.push(
        `${t('width', 'Width')}: R +${orderData?.overWidthRight ?? 0} / L +${orderData?.overWidthLeft ?? 0}`,
      )
    }

    if (orderData?.overLengthFront || orderData?.overLengthRear) {
      lines.push(
        `${t('length', 'Length')}: ${t('front', 'front')} +${orderData?.overLengthFront ?? 0} / ${t('rear', 'rear')} +${orderData?.overLengthRear ?? 0}`,
      )
    }

    if (orderData?.overHeight) {
      lines.push(`${t('height', 'Height')}: +${orderData.overHeight}`)
    }

    if (orderData?.unloadingMethod) {
      lines.push(
        `${t('unloading', 'Unloading')} : ${t(orderData.unloadingMethod, orderData.unloadingMethod)}`,
      )
    }

    if (!lines.length) return t('outOfGauge', 'Out of gauge')

    return (
      <>
        <div>{t('outOfGauge', 'Out of gauge')}</div>
        {lines.map((line, i) => (
          <div key={i}>{line}</div>
        ))}
      </>
    )
  })()

  const categoryVisits = computed(() => {
    if (inboundVisit && outboundVisit)
      return `${t('in', 'In')}: ${inboundVisit} / ${t('out', 'Out')}: ${outboundVisit}`

    if (inboundVisit) return `${t('in', 'In')}: ${inboundVisit}`

    return undefined
  }).get()

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 1,
        p: 1,
        border: '1px solid',
        borderColor: highlight ? theme.palette.info.main : theme.palette.grey[400],
        background: schemeBackgroundColor ?? 'white',
        minHeight: '145px',
        width: '100%',
        opacity: !isHighlighting || highlight ? 1 : 0.3,
      }}
    >
      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        <Box>
          {(() => {
            const portValue = direction === 'Inbound' ? pol : pod
            return portValue ? (
              <Tooltip title={portValue} enterDelay={500}>
                <Typography variant='body2' fontSize={'0.7rem'}>
                  {portValue}
                </Typography>
              </Tooltip>
            ) : (
              <Typography variant='body2' fontSize={'0.7rem'}>
                {portValue}
              </Typography>
            )
          })()}
        </Box>

        <Stack direction='row' alignItems='center' spacing={0.5}>
          {isReserved ? (
            <ColoredLabel label={t('reserved', 'Reserved')} color='info' />
          ) : (
            <ContainerJourneyStatusChip status={status} />
          )}
          {renderActions}
        </Stack>
      </Stack>

      <Stack direction='row' justifyContent='space-between' alignItems='baseline'>
        <ContainerNumber
          value={number}
          variant='h6'
          sx={{ fontWeight: 'bold', lineHeight: 1.2, fontSize: '1rem' }}
        />
        {category && (
          <Typography
            variant='body2'
            fontSize={'0.65rem'}
            color={theme.palette.text.secondary}
            title={categoryVisits}
          >
            {category}
          </Typography>
        )}
      </Stack>

      <Stack direction='row' spacing={1} alignItems='center' mt={0.3} sx={{ minHeight: '15px' }}>
        <Typography variant='body2' noWrap fontSize={'0.7rem'}>
          {shippingLine}
        </Typography>
        <Typography variant='body2' noWrap fontSize={'0.7rem'}>
          {consignee}
        </Typography>
        <Typography variant='body2' noWrap fontSize={'0.7rem'}>
          {referenceNumber}
        </Typography>
      </Stack>

      <Stack direction='row' justifyContent='space-between' alignItems='center' mt={0.3}>
        <Stack direction='row' spacing={0.5} alignItems='center'>
          {archetype ? (
            <Tooltip title={archetype} enterDelay={500}>
              <Typography variant='body2' fontSize={'0.7rem'}>
                {isoCode}
              </Typography>
            </Tooltip>
          ) : (
            <Typography variant='body2' fontSize={'0.7rem'}>
              {isoCode}
            </Typography>
          )}
          {archetype && (
            <Typography variant='body2' fontSize={'0.65rem'} color={theme.palette.text.secondary}>
              {archetype}
            </Typography>
          )}
        </Stack>
        <Stack direction='row' spacing={0.5} alignItems='center'>
          <Typography variant='body2' fontSize={'0.7rem'}>
            {weight}kg
          </Typography>
          {weightClass && (
            <Typography variant='body2' fontSize={'0.65rem'} color={theme.palette.text.secondary}>
              {weightClass}
            </Typography>
          )}
        </Stack>
      </Stack>

      {finalDestination && (
        <Stack direction='row' spacing={1} alignItems='center' mt={0.3} sx={{ minHeight: '15px' }}>
          {finalDestination && (
            <Typography
              variant='body2'
              noWrap
              fontSize={'0.65rem'}
              color={theme.palette.text.secondary}
            >
              {finalDestination}
            </Typography>
          )}
        </Stack>
      )}

      <Stack direction='row' spacing={1} alignItems='center' mt={0.3}>
        <Tooltip
          title={
            isEmpty ? t('emptyContainer', 'Empty container') : t('fullContainer', 'Full container')
          }
          enterDelay={500}
        >
          <Box component='span' sx={{ display: 'flex' }}>
            <IconFullOrEmptyContainer isEmpty={!!isEmpty} />
          </Box>
        </Tooltip>

        {isDangerous && (
          <Tooltip title={dangerousTooltip} enterDelay={500}>
            <Box component='span' sx={{ display: 'flex' }}>
              <DangerousIcon />
            </Box>
          </Tooltip>
        )}

        {isReefer && (
          <Tooltip title={t('reeferContainer', 'Reefer / temperature controlled')} enterDelay={500}>
            <Box component='span' sx={{ display: 'flex' }}>
              <ReeferIcon />
            </Box>
          </Tooltip>
        )}

        {isDamaged && (
          <Tooltip title={t('damagedContainer', 'Container reported as damaged')} enterDelay={500}>
            <Box component='span' sx={{ display: 'flex' }}>
              <DamagedIcon />
            </Box>
          </Tooltip>
        )}

        {hasIssue && (
          <Tooltip title={t('containerHasIssue', 'Container has issue')} enterDelay={500}>
            <Stack color={theme.palette.warning.main}>
              <WarningIcon />
            </Stack>
          </Tooltip>
        )}

        {isOog && (
          <Tooltip title={oogTooltip} enterDelay={500}>
            <Box
              sx={{
                ...iconBoxStyle,
                backgroundColor: 'black',
                borderRadius: '12px',
                padding: '0 8px',
                cursor: 'default',
              }}
            >
              OOG
            </Box>
          </Tooltip>
        )}

        {hasHolds && (
          <HoldsIconByJourneyId containerJourneyId={orderData?.holds?.[0]?.containerJourneyId} />
        )}
      </Stack>
    </Paper>
  )
}
